<script lang="ts" setup>
import Dialog from '@jaaluu/components/Dialog.vue';
import NothingFound from '@jaaluu/components/NothingFound.vue';
import TextWithTooltips from '@jaaluu/components/TextWithTooltips.vue';
import { GAnalyticsEvent } from '@jaaluu/interfaces/GAnalyticEvent';
import stores from '@jaaluu/stores';
import { FavWord } from '@jaaluu/stores/favorites';
import vuetify from '@jaaluu/vuetify';
import { t } from '@shared/i18n';
import { Language, LanguageLevel, LangVoiceType } from '@shared/openapi/jaaluu-api';
import sharedStores from '@shared/shared-stores';
import { computed, nextTick, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FavGroupSelect from './FavGroupSelect.vue';

const props = defineProps<{ initialTab?: string }>();
const emits = defineEmits<{
  (e: 'close'): void;
}>();

const router = useRouter();
const route = useRoute();

const { saveNotifyMeSrsInLocalStorage } = stores.settings.funcs;
const {
  toggleFavoriteWord,
  getSuccessRateColor,
  getDateToNextReview,
  deleteFavoriteGroup,
  setFavWordsForReviewSrc,
  moveWordToSrsLevel
} = stores.favorites.funcs;
const { getHumanizedDate } = sharedStores.datetime.funcs;
const { curriculum, courseStatus } = stores.course.state;
const { wordsToLearn, favoriteGroups, favWordsForReviewSrc, activeGroupKey } =
  stores.favorites.state;
const { SRS_INTERVALS, FIXED_FAV_GROUPS } = stores.favorites.consts;
const { notifyMeSrs, playbackRate } = stores.settings.state;
const { autoText } = stores.autoText.state;
const { langForeign, langNative, langLevel } = stores.language.state;
const { getAudioFileUrl } = stores.files.funcs;
const { sendEvent } = stores.gAnalytics.funcs;

const selectedFavGroup = ref(activeGroupKey.value);
const headers: Ref<{ key: string; title: string; sortable?: boolean }[]> = ref([]);
const sortByWords = ref([{ key: 'added_at', order: 'desc' }]);
const filterSrs = ref([1, 7]);
const filterSucess = ref([0, 100]);
const filterStoryIdx: Ref<number | null> = ref(route.query.storyIdx ? +route.query.storyIdx : null);
const openMenus: Ref<Record<string, boolean>> = ref({});
const filteredWordsLang: Ref<FavWord[]> = ref([]);
const showWhatIsSrs = ref(false);
const audioIsGenerating = ref(false);
const audioWordIndex = ref(0);
const audioPlayer = new Audio();
const isCourseList = computed(() => {
  console.log('route.params.type', route.params.type);
  return route.params.type === 'course';
});

const favWordsLang = computed(() => {
  return (favoriteGroups.value[selectedFavGroup.value]?.words ?? []).filter(
    (f) => f.langForeign === langForeign.value
  );
});

const getFavWordsLang = (groupName: string) => {
  return (favoriteGroups.value[groupName]?.words ?? []).filter(
    (f) => f.langForeign === langForeign.value
  );
};

const filterFavWords = () => {
  if (!favoriteGroups.value[selectedFavGroup.value]) {
    filteredWordsLang.value = [];
    return;
  }
  let _filtered = favoriteGroups.value[selectedFavGroup.value].words
    .filter(
      (word) => word.srs_level + 1 >= filterSrs.value[0] && word.srs_level + 1 <= filterSrs.value[1]
    )
    .filter(
      (word) =>
        word.success_rate >= filterSucess.value[0] && word.success_rate <= filterSucess.value[1]
    );
  if (filterStoryIdx.value !== null) {
    _filtered = _filtered.filter((fW) => fW.storyIdx == filterStoryIdx.value);
  }
  filteredWordsLang.value = _filtered;
  setFavWordsForReviewSrc();
};

onMounted(() => {
  wordsToLearn.value = [];
  setHeaders();
  filterFavWords();
  if (langLevel.value === LanguageLevel.A1_A2 && courseStatus.value.isActive) {
    selectedFavGroup.value = 'course';
  }
});

onUnmounted(() => {
  stopAudioGeneration();
});

const setHeaders = () => {
  let _headers = [
    {
      key: 'word',
      title: t('favorites.word')
    },
    {
      key: 'translations',
      title: t('favorites.translation')
    },
    {
      key: 'wordType',
      title: t('favorites.word_type')
    },
    {
      key: 'srs_level',
      title: t('favorites.srs_level')
    },
    {
      key: 'success_rate',
      title: t('favorites.success_rate')
    },
    {
      key: 'last_seen',
      title: t('favorites.last_seen')
    },
    {
      key: 'next_review',
      title: t('favorites.next_review')
    },
    {
      key: 'context_sentence',
      title: t('favorites.context')
    },
    {
      key: 'storyIdx',
      title: t('favorites.short_story')
    },
    {
      key: 'actions',
      title: '',
      sortable: false
    }
  ];
  if (!isCourseList.value) {
    _headers = _headers.filter((h) => !['mainCategory', 'subCategory'].includes(h.key));
  }
  headers.value = _headers;
};

const visibleFavGroups = computed(() =>
  Object.keys(favoriteGroups.value).filter(
    (fg) => !['phrases', 'sayings'].includes(fg) || getFavWordsLang(fg).length > 0
  )
);

const moveFavTo = (fav: FavWord, newGroupName: string) => {
  fav.groupName = newGroupName;
  favoriteGroups.value[selectedFavGroup.value].words = favoriteGroups.value[
    selectedFavGroup.value
  ].words.filter((f) => fav !== f);
  favoriteGroups.value[newGroupName].words.push(fav);
  filterFavWords();
};

const _deleteFavoriteGroup = (groupName: string) => {
  selectedFavGroup.value = 'default';
  deleteFavoriteGroup(groupName);
};

const startRepeat = (favGroup: string = selectedFavGroup.value) => {
  sendEvent(GAnalyticsEvent.CLICK_START_TRAINING_FAV_WORDS, { trainType: 'srs', favGroup });
  router.push({
    name: 'learn-repeat',
    query: { favGroup }
  });
};

const startLearn = (trainType: 'flipcards' | 'exercises' | 'text-with-words') => {
  const favGroup = selectedFavGroup.value;
  sendEvent(GAnalyticsEvent.CLICK_START_TRAINING_FAV_WORDS, { trainType, favGroup });
  wordsToLearn.value = filteredWordsLang.value;
  if (trainType === 'exercises') {
    router.push({
      name: 'learn-repeat',
      query: { favGroup }
    });
  } else if (['flipcards'].includes(trainType)) {
    router.push({
      name: 'learn-flipcards',
      query: { favGroup }
    });
  } else {
    autoText.value.inclueWords = filteredWordsLang.value.map((w) => w.word);
    router.push(`/${langForeign.value}/auto-text`);
  }
  // Deferred for the same reason as LevelSettings.vue: this fires from inside a v-menu
  // item click, so the menu's own teleported overlay is still closing this tick. Closing
  // the parent v-dialog in the same flush unmounts two teleported overlays at once and
  // Vue throws "insertBefore" on a null parent.
  nextTick(() => {
    emits('close');
  });
};

const playAudioFile = (text: string, lang: Language) => {
  return new Promise<void>((resolve) => {
    const src = getAudioFileUrl(text, lang, LangVoiceType.WOMAN);
    if (!src) {
      resolve();
      return;
    }
    audioPlayer.src = src;
    audioPlayer.playbackRate = playbackRate.value;
    audioPlayer.onended = () => resolve();
    audioPlayer.play();
  });
};

const waitMs = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const compareSortValues = (a: unknown, b: unknown): number => {
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.join(', ').localeCompare(b.join(', '));
  }
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const getSortedWordsForAudio = (): FavWord[] => {
  const words = [...filteredWordsLang.value];
  words.sort((a, b) => {
    for (const { key, order } of sortByWords.value) {
      const cmp = compareSortValues(a[key as keyof FavWord], b[key as keyof FavWord]);
      if (cmp !== 0) return order === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
  return words;
};

const stopAudioGeneration = () => {
  audioIsGenerating.value = false;
  audioPlayer.pause();
  audioPlayer.src = '';
};

const playAudioGenerationLoop = async () => {
  const words = getSortedWordsForAudio();
  while (audioIsGenerating.value && audioWordIndex.value < words.length) {
    const word = words[audioWordIndex.value];
    await playAudioFile(word.word, word.langForeign);
    if (!audioIsGenerating.value) return;
    await playAudioFile(word.translations.join(', '), word.langNative);
    if (!audioIsGenerating.value) return;
    await waitMs(1000);
    if (!audioIsGenerating.value) return;
    audioWordIndex.value += 1;
  }
  audioIsGenerating.value = false;
};

const startAudioGeneration = () => {
  stopAudioGeneration();
  audioWordIndex.value = 0;
  audioIsGenerating.value = true;
  playAudioGenerationLoop();
};

watch(
  () => langForeign.value,
  () => {
    setHeaders();
  }
);
watch(
  () => filterSrs.value[0],
  () => {
    filterFavWords();
  }
);
watch(
  () => filterSrs.value[1],
  () => {
    filterFavWords();
  }
);
watch(
  () => filterSucess.value[0],
  () => {
    filterFavWords();
  }
);
watch(
  () => filterSucess.value[1],
  () => {
    filterFavWords();
  }
);
watch(
  () => route.params.type,
  () => {
    filterFavWords();
  }
);
watch(
  () => selectedFavGroup.value,
  () => {
    filterFavWords();
  }
);
watch(
  () => notifyMeSrs.value,
  () => {
    saveNotifyMeSrsInLocalStorage(notifyMeSrs.value);
  }
);
watch(
  () => favoriteGroups.value,
  () => {
    filterFavWords();
  },
  { deep: true }
);
watch(
  () => filterStoryIdx.value,
  () => {
    filterFavWords();
  }
);
</script>
<template>
  <div class="" v-if="langForeign && langLevel">
    <div class="mt-2">
      <v-alert
        color="purple"
        variant="tonal"
        border="start"
        icon="mdi-brain"
        v-if="favWordsForReviewSrc.length > 4"
      >
        <div class="d-flex align-center flex-wrap">
          <div class="mr-6">
            {{ t('tour_guide.repeat_words.text', { nWords: favWordsForReviewSrc.length }) }}
          </div>

          <v-btn color="purple" size="small" @click="() => startRepeat('srs')">{{
            t('tour_guide.repeat_words.call_to_action')
          }}</v-btn>
        </div></v-alert
      >
    </div>

    <div>
      <div
        class="d-flex justify-center align-end mt-2 min-h-buttons mb-4"
        v-if="filteredWordsLang.length > 4"
      >
        <div class="d-block d-md-flex">
          <v-menu id="word-links">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="green"
                class=""
                size="small"
                variant="tonal"
                prepend-icon="mdi-weight-lifter"
                >{{ t('favorites.train_words', { wordCount: filteredWordsLang.length }) }}</v-btn
              >
            </template>
            <v-list color="green" density="compact">
              <v-list-item @click="() => startLearn('flipcards')" prepend-icon="mdi-card-multiple">
                <v-list-item-title class="pr-12"> {{ t('train.flashcards') }}</v-list-item-title>
              </v-list-item>

              <v-list-item prepend-icon="mdi-dumbbell" @click="() => startLearn('exercises')">
                <v-list-item-title class="pr-12"> {{ t('train.exercises') }}</v-list-item-title>
              </v-list-item>
              <v-list-item prepend-icon="mdi-text" @click="() => startLearn('text-with-words')">
                <v-list-item-title class="pr-12">
                  {{ t('train.text_with_words') }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item prepend-icon="mdi-volume-high" @click="() => startAudioGeneration()">
                <v-list-item-title class="pr-12">
                  {{ t('train.generate_audio') }}</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="d-flex align-center ml-2 mt-2" v-if="audioIsGenerating">
            <v-progress-circular
              indeterminate
              class="mr-2"
              density="compact"
              width="1"
              size="12"
              color="green"
            ></v-progress-circular>
            <span class="mr-2 gray-text"
              >{{ t('train.generate_audio') }}: {{ audioWordIndex + 1 }} /
              {{ filteredWordsLang.length }}</span
            >
            <v-btn
              size="x-small"
              variant="tonal"
              color="red"
              prepend-icon="mdi-stop"
              @click="() => stopAudioGeneration()"
              >{{ t('train.stop_audio') }}</v-btn
            >
          </div>

          <!-- <v-menu id="word-links" v-if="filteredWordsLang.length > 3">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="green"
                class="mr-6"
                size="small"
                variant="outlined"
                prepend-icon="mdi-weight-lifter"
                >{{ t('favorites.test') }}</v-btn
              >
            </template>
            <v-list color="green" density="compact">
              <v-list-group :value="t('train.exercises')" v-if="favWordsLang.length > 3">
                <template v-slot:activator="{ props }">
                  <v-list-item
                    prepend-icon="mdi-school"
                    v-bind="props"
                    :title="t('train.exercises')"
                  ></v-list-item>
                </template>
                <v-list-item
                  @click="() => startLearn('filtered', 'exercises')"
                  v-if="favWordsLang.length > 3"
                >
                  <v-list-item-title class="pr-12"
                    >All: {{ favWordsLang.length }}
                    {{ tc('general.words', favWordsLang.length) }}</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  @click="() => startLearn('srs', 'exercises')"
                  v-if="favWordsLang.length > 3 && srsWordCount > 0 && !srsWordsEqualAllWords"
                >
                  <v-list-item-title class="pr-12"
                    >Srs: {{ srsWordCount }}
                    {{ tc('general.words', srsWordCount) }}</v-list-item-title
                  >
                </v-list-item>
              </v-list-group>
            </v-list>
          </v-menu> -->
        </div>
      </div>
      <div class="d-flex">
        <v-tabs class="mb-6" v-model="selectedFavGroup" align-tabs="center" color="primary">
          <v-tab :value="fg" v-for="fg in visibleFavGroups" class="mr-3">
            <v-badge
              id="fav-badge"
              offset-x="-8"
              offset-y="-8"
              :color="getFavWordsLang(fg).length > 0 ? 'primary' : 'transparent'"
              :content="getFavWordsLang(fg).length > 0 ? getFavWordsLang(fg).length : ''"
            >
              {{ fg }}
              <v-icon
                icon="mdi-delete"
                class="ml-2"
                v-if="!FIXED_FAV_GROUPS.includes(fg)"
                @click.stop="() => _deleteFavoriteGroup(fg)"
              />
            </v-badge>
          </v-tab>
        </v-tabs>
        <FavGroupSelect class="mr-2 mr-md-16" :show-add-button="true" :show-select="false" />
      </div>

      <div
        class="d-flex flex-wrap mt-4 justify-center align-start range-max-wrapper"
        v-if="favWordsLang.length > 0"
      >
        <div>
          <div class="gray-text">{{ t('favorites.srs_level') }}</div>
          <v-range-slider
            class="range-max"
            show-ticks
            thumb-label
            v-model="filterSrs"
            strict
            :max="8"
            thumb-size="10"
            track-size="2"
            :min="1"
            color="green"
            :step="1"
          ></v-range-slider>
        </div>
        <div class="ml-6">
          <div class="gray-text">{{ t('favorites.success_rate') }}</div>
          <v-range-slider
            class="range-max"
            color="green"
            v-model="filterSucess"
            thumb-label
            thumb-size="10"
            track-size="2"
            :max="100"
            :min="0"
            :step="25"
          ></v-range-slider>
        </div>
        <div v-if="selectedFavGroup === 'course'" class="ml-4">
          <v-select
            density="compact"
            variant="underlined"
            class="w-fix-150"
            :label="t('favorites.short_story')"
            clearable
            hide-details
            v-model="filterStoryIdx"
            :items="curriculum.map((c, idx) => ({ value: idx, title: `${idx + 1}. ${c.title}` }))"
            color="green"
          ></v-select>
        </div>
      </div>

      <NothingFound v-if="favWordsLang.length === 0" :t_key="'favorites.no_favorites_found'" />
      <div v-else>
        <v-data-table
          :items="filteredWordsLang"
          :headers="headers"
          v-model:sort-by="sortByWords"
          :height="vuetify.display.smAndUp.value ? 'auto' : 300"
          density="compact"
        >
          <template v-slot:item.word="{ item }">
            <TextWithTooltips :text="item.word" class="text-green bold" />
          </template>
          <template v-slot:item.translations="{ item }">
            <span class="bold">
              {{ item.translations.join(', ') }}
            </span>
          </template>
          <template v-slot:item.last_seen="{ item }">
            <span>{{ getHumanizedDate(item.last_seen.toString(), langNative!) }}</span>
          </template>
          <template v-slot:item.next_review="{ item }">
            <span>{{
              getHumanizedDate(
                getDateToNextReview(item.srs_level, item.last_level_change).toISOString(),
                langNative!
              )
            }}</span>
          </template>
          <template v-slot:item.srs_level="{ item }">
            <v-chip size="small" color="purple">{{ item.srs_level + 1 }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-menu v-model="openMenus[item.word]" :close-on-content-click="false" class="menu">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon="mdi-dots-vertical"
                    size="x-small"
                    variant="text"
                    v-bind="props"
                  ></v-btn>
                </template>

                <v-list density="compact">
                  <v-list-group v-if="langLevel !== LanguageLevel.A1_A2">
                    <template v-slot:activator="{ props }">
                      <v-list-item
                        prepend-icon="mdi-arrow-right-bold"
                        v-bind="props"
                        :title="'Move to category'"
                      ></v-list-item>
                    </template>
                    <v-list-item
                      v-for="fg in Object.keys(favoriteGroups)"
                      :key="fg"
                      :title="fg"
                      :value="fg"
                      @click="() => moveFavTo(item, fg)"
                    >
                    </v-list-item>
                  </v-list-group>
                  <v-list-group>
                    <template v-slot:activator="{ props }">
                      <v-list-item
                        prepend-icon="mdi-stairs-up"
                        v-bind="props"
                        :title="'Move to SRS-Level'"
                      ></v-list-item>
                    </template>
                    <v-list-item
                      v-for="lvl in 8"
                      :key="lvl"
                      :title="`Level ${lvl}`"
                      :value="lvl"
                      @click="
                        () => {
                          moveWordToSrsLevel(item.word, item.wordType, selectedFavGroup, lvl - 1);
                          filterFavWords();
                          openMenus[item.word] = false;
                        }
                      "
                    >
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-menu>

              <v-btn
                @click="
                  () => {
                    toggleFavoriteWord(
                      item.word,
                      langForeign!,
                      langNative!,
                      item.wordType,
                      'to-remove',
                      -1,
                      true,
                      selectedFavGroup,
                      item.context_sentence
                    );
                    filterFavWords();
                  }
                "
                size="x-small"
                variant="text"
                color="grey"
                icon="mdi-close"
              />
            </div>
          </template>
          <template v-slot:item.success_rate="{ item }">
            <div
              class="d-flex align-center"
              :class="[
                getSuccessRateColor(item.success_rate),
                vuetify.display.smAndUp.value ? null : 'justify-end'
              ]"
            >
              <v-progress-circular
                :model-value="item.success_rate"
                size="20"
                :color="getSuccessRateColor(item.success_rate)"
                background-color="grey lighten-2"
                class="mr-2"
              ></v-progress-circular>
              <span class="mr-2" v-if="item.trained_count > 0">{{ item.success_rate }}%</span>
              <span class="subtitle">{{ item.trained_success }} / {{ item.trained_count }}</span>
            </div>
          </template>
          <template v-slot:item.context_sentence="{ item }">
            <v-icon
              v-if="item.context_sentence"
              icon="mdi-information-outline"
              size="12"
              color="gray"
              class="ml-1 grab"
              v-tooltip="`${item.context_sentence}`"
            />
          </template>
          <template v-slot:item.storyIdx="{ item }">
            <div v-if="item.storyIdx > -1">
              {{ item.storyIdx + 1 }}. {{ curriculum[item.storyIdx]?.title }}
            </div>
          </template>
        </v-data-table>
        <div class="d-flex justify-end align-center">
          <div class="d-flex justify-end mr-6">
            <v-checkbox
              class=""
              color="green"
              hide-details
              density="compact"
              v-model="notifyMeSrs"
              :label="t('favorites.notify_me_srs')"
            />
          </div>
          <v-btn size="x-small" variant="text" color="green" @click="() => (showWhatIsSrs = true)">
            {{ t('favorites.what_is_srs') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    :max-width="1000"
    v-model="showWhatIsSrs"
    :title="t('favorites.what_is_srs')"
    @close="() => (showWhatIsSrs = false)"
  >
    <div>{{ t('favorites.srs_short') }}</div>
    <div class="mt-5">{{ t('favorites.srs_long') }}</div>
    <div class="mt-2">
      {{
        t('favorites.srs_intervals', {
          intervals: SRS_INTERVALS.join(', '),
          nIntervals: SRS_INTERVALS.length
        })
      }}
    </div>

    <div class="mt-16 title-sm">{{ t('favorites.when_does_a_word_move') }}</div>
    <div class="mt-2">{{ t('favorites.srs_next_level_example_1') }}</div>
    <div class="mt-4">
      {{
        t('favorites.srs_next_level_example_2', { level: 2, levelM1: 1, days: SRS_INTERVALS[1] })
      }}
    </div>
    <div class="mt-2">
      {{
        t('favorites.srs_next_level_example_2', { level: 3, levelM1: 2, days: SRS_INTERVALS[2] })
      }}
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.flag-icon {
  width: 25px;
}

.height-fav-url {
  height: 80px;
}
.read-view-button {
  width: 100px;
}
.red {
  color: red !important;
}
.green {
  color: green !important;
}
.orange {
  color: orange !important;
}
.range-max {
  width: 80px;
  max-width: 100px;
}
@media screen and (min-width: 400px) {
  .range-max {
    width: 120px;
    max-width: 150px;
  }
}
.min-h-buttons {
  min-height: 28px;
}
</style>
